import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import DeleteWithConfirm from "./DeleteWithConfirm";
import { OwnershipTableRowData } from "./UserGroupDetails.types";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

interface OwnershipGroupedTableProps {
  items: OwnershipTableRowData[];
  expandedGroups: Record<string, boolean>;
  toggleGroup: (groupName: string) => void;
  getUserName: (userId: string) => string;
  canManageOwnership: boolean;
  groupId?: string;
  refetch: () => void;
}

const OwnershipGroupedTable = ({
  items,
  expandedGroups,
  toggleGroup,
  getUserName,
  canManageOwnership,
  groupId,
  refetch,
}: OwnershipGroupedTableProps) => {
  return (
    <DataTable<OwnershipTableRowData>
      onRowClick={item => {
        if ("isGroupHeader" in item && item.isGroupHeader && Number(item.count) > 1) {
          toggleGroup(item.grouping || "ungrouped");
        }
      }}
      data={{ items }}
      header={[
        {
          id: 0,
          uid: "rule",
          label: "Rule",
          render: item => <div>{item.ruleOperator || ""}</div>,
        },
        {
          id: 1,
          uid: "entity_type",
          label: "Entity Type",
          render: item => {
            const isIndented = item.ruleOperator === "AND";
            if ("isGroupHeader" in item && item.isGroupHeader) {
              const groupKey = item.grouping || "ungrouped";
              const isExpanded = expandedGroups[groupKey];
              return (
                <Stack direction="row" align="center">
                  <div>{item.entity_type}</div>
                  {Number(item.count) > 1 && (
                    <Stack
                      direction="row"
                      align="center"
                      className="al-ml-2 al-text-gray-500"
                      aria-label={isExpanded ? "Collapse group" : "Expand group"}
                    >
                      {isExpanded ? (
                        <IconChevronUp size={16} aria-hidden="true" />
                      ) : (
                        <IconChevronDown size={16} aria-hidden="true" />
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            }
            return <div className={isIndented ? "al-pl-6" : ""}>{item.entity_type}</div>;
          },
        },
        {
          id: 2,
          uid: "condition_key",
          label: "Condition Key",
          render: item => <div>{item.ownership_rule.condition_key}</div>,
        },
        {
          id: 3,
          uid: "condition_type",
          label: "Condition Type",
          render: item => <div>{item.ownership_rule.condition_type}</div>,
        },
        {
          id: 4,
          uid: "condition_value",
          label: "Condition Value",
          render: item => (
            <div className="al-break-words">
              {item.ownership_rule.condition_key === "tag" &&
              item.ownership_rule.condition_key_value
                ? `${item.ownership_rule.condition_key_value}: ${item.ownership_rule.condition_value}`
                : item.ownership_rule.condition_value}
            </div>
          ),
        },
        {
          id: 5,
          uid: "created",
          label: "Created",
          render: item => (
            <div>
              <div>
                <b>{getUserName(item.created_by)}</b>
              </div>
              <div>{dayjs(item.created_at).format("DD MMM YYYY hh:mm")}</div>
            </div>
          ),
        },
        {
          id: 6,
          uid: "updated",
          label: "Updated",
          render: item => (
            <div>
              <div>
                <b>{getUserName(item.updated_by)}</b>
              </div>
              <div>{dayjs(item.updated_at).format("DD MMM YYYY hh:mm")}</div>
            </div>
          ),
        },
        {
          id: 7,
          uid: "actions",
          label: "Actions",
          render: item => (
            <div>
              {canManageOwnership ? (
                <DeleteWithConfirm
                  entityId={item.id}
                  id={groupId || ""}
                  refetch={refetch}
                  entityType="ownership"
                  title={`Delete ownership ${item.ownership_rule.condition_value}`}
                />
              ) : null}
            </div>
          ),
        },
      ]}
    />
  );
};

export default OwnershipGroupedTable;
